import { Popover, PopoverContent, PopoverTrigger } from '@chakra-ui/popover';
import { MenuItem } from '@components/menu';
import { CheckThinIcon, CloseIcon, DeleteIcon, SearchIcon, TagsIcon } from '@sketchnote/icons';
import { Input, MoreActionMenu } from '@sketchnote/shared-components';
import Tippy from '@tippyjs/react';
import cn from 'classnames';
import _ from 'lodash';
import castArray from 'lodash/castArray';
import { useRouter } from 'next/router';
import * as React from 'react';
import { useBoardStore } from 'src/features/boards';
import { useTaskAccess } from 'src/features/files/hooks/useTaskAccess';
import { ColumnToolbarLabel } from 'src/features/kanban/component/Column/Toolbar';
import { v4 as uuid } from 'uuid';

import { useTasksStore } from '../store/useTasksStore';

const { useCallback } = React;

const randomColor = {
  Pink: '#fbb6ce',
  Purple: '#d6bcfa',
  Blue: '#9BADEE',
  Teal: '#81e6d9',
  Green: '#86E495',
  Yellow: '#FFE98F',
  Orange: '#fbd38d',
  Red: '#FBB1A7',
};
const randomColorClass = {
  '#fbb6ce': 'bg-pink-300',
  '#d6bcfa': 'bg-purple-300',
  '#9BADEE': 'bg-blue-300',
  '#81e6d9': 'bg-teal-300',
  '#86E495': 'bg-green-300',
  '#FFE98F': 'bg-yellow-300',
  '#fbd38d': 'bg-orange-300',
  '#FBB1A7': 'bg-red-300',
};

export const EditItemLabel = () => {
  const taskId = useTasksStore((s) => s.currentTaskId);
  const boardsStore = useBoardStore();
  const onUpdateItemField = useTasksStore((s) => s.onUpdateItemField);
  const fetchLabel = useBoardStore((s) => s.actions.fetchLabel);
  const { labels } = useBoardStore((s) => ({
    labels: s.labels,
  }));
  const [showPopover, setShowPopover] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const router = useRouter();
  const inputRef = React.useRef<HTMLInputElement>(null);

  const { isReadOnly } = useTaskAccess(taskId || '');

  const item = useTasksStore(
    useCallback(
      (s) => {
        if (taskId) {
          return s.tasks[taskId];
        }
      },
      [taskId]
    )
  );

  const selectedLabelOptions = React.useMemo(() => {
    const label = item?.label ? castArray(item.label) : [];

    return label
      .map((item) => {
        const labelObj = labels.find((obj) => obj.id === item.id);

        return labelObj;
      })
      .filter(Boolean);
  }, [item?.label, labels]);

  const handleToggleLabel = async (item) => {
    if (isReadOnly) return;
    if (selectedLabelOptions.find((selected) => selected.id === item.id)) {
      await handleChange(selectedLabelOptions.filter((obj) => obj.id !== item.id));
    } else {
      await handleChange([...selectedLabelOptions, item]);
    }
  };

  const handleChange = async (value) => {
    const colors = Object.values(randomColor);
    const updatedLabels = value.map((item) =>
      item.color
        ? item
        : {
            label: item.label,
            value: item.value,
            color: colors[Math.floor(Math.random() * colors.length)],
            id: uuid(),
          }
    );

    await onUpdateItemField({
      item: {
        label: updatedLabels.map((item) => ({ label: item.label, color: item.color, id: item.id })),
        id: item?.id || '',
      },
      keys: ['label'],
    });
    fetchLabel({ id: router?.query?.view as string });
  };

  React.useEffect(() => {
    if (!labels.length) {
      fetchLabel({ id: router?.query?.view as string });
    }
  }, []);

  const filteredLabels = React.useMemo(() => {
    return labels.filter(
      (item) => !search || item.label.toLowerCase().includes(search.toLowerCase())
    );
  }, [labels, search]);

  return (
    <div>
      <div className="flex items-center gap-12">
        <Popover
          placement="bottom-start"
          isOpen={!isReadOnly && showPopover}
          onClose={() => setShowPopover(false)}
        >
          <PopoverTrigger>
            <Tippy
              disabled={!isReadOnly}
              content={'You do not have the required permission to edit tasks'}
            >
              <button
                onClick={() => {
                  setShowPopover((p) => !p);
                  setSearch('');
                  setTimeout(() => {
                    inputRef.current?.focus();
                  }, 100);
                }}
                className="p-6 rounded-full border"
              >
                <TagsIcon size={12} />
              </button>
            </Tippy>
          </PopoverTrigger>
          <PopoverContent>
            <div className="bg-white border rounded-lg max-w-250">
              <div className="px-12 py-8">
                <Input
                  leftIcon={<SearchIcon className="ml-12" />}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search for option"
                  inputProps={{
                    onKeyDown: (e) => {
                      if (e.key === 'Enter') {
                        if (!search) return setShowPopover(false);
                        const alreadyExists = labels.find(
                          (item) => item.label.toLowerCase() === search.toLowerCase()
                        );
                        if (alreadyExists) {
                          handleToggleLabel(alreadyExists);
                        } else
                          handleToggleLabel({
                            label: search,
                            value: search,
                          });
                        setSearch('');
                      }
                    },
                    ref: inputRef,
                  }}
                />
              </div>
              {!filteredLabels.length && !search && (
                <div className="text-center text-gray-500 py-12">No labels found</div>
              )}
              {filteredLabels.map((item, index) => {
                const isSelected = !!selectedLabelOptions?.find((obj) => obj.id === item.id);
                return (
                  <LabelMenuOption
                    key={index}
                    item={item}
                    isSelected={isSelected}
                    onSelect={handleToggleLabel}
                  />
                );
              })}
              {search && !labels.find((item) => item.label.toLowerCase() === search.toLowerCase()) && (
                <MenuItem
                  className="flex items-center gap-8 border-t rounded-t-none"
                  onClick={() =>
                    handleToggleLabel({
                      label: search,
                      value: search,
                    })
                  }
                >
                  Add <span className="text-primary-500">&quot;{search}&quot;</span>
                </MenuItem>
              )}
            </div>
          </PopoverContent>
        </Popover>
        <div className="flex items-center gap-8 flex-grow">
          {selectedLabelOptions.map((item, index) => {
            return (
              <div key={index}>
                <div
                  className="relative px-8 py-4 rounded-full text-xs bg-opacity-25 text-gray-999 group"
                  style={{
                    backgroundColor: item.color,
                  }}
                >
                  {!isReadOnly && (
                    <div
                      className="absolute cursor-pointer bg-white rounded-full w-12 h-12 flex items-center opacity-0 group-hover:opacity-100"
                      style={{ top: -4, right: -4 }}
                      onClick={() => handleToggleLabel(item)}
                    >
                      <CloseIcon size={8} />
                    </div>
                  )}
                  {item.label}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {/* <CreatableSelect
        isClearable
        isMulti
        isDisabled={item?.isReadOnly || item?.access === 'view' || boardsStore.isPublicView}
        placeholder="Select/Create labels"
        className="mt-14 mb-10"
        menuPlacement="auto"
        options={[
          {
            label: 'Select an option or create one',
            options: labels,
          },
        ]}
        menuPortalTarget={document.body}
        onChange={handleChange}
        value={selectedLabelOptions}
        components={{
          DropdownIndicator:
            item?.isReadOnly || item?.access === 'view' || boardsStore.isPublicView
              ? null
              : UserSelectDropDownIndicator,
          IndicatorSeparator: () => null,
          NoOptionsMessage: LabelSelectNoOptions,
          SingleValue: LabelSelectSingleValue,
          Option: LabelSelectOption,
        }}
        styles={labelSelectStyles}
      /> */}
    </div>
  );
};

type LabelOption = { color: string; label: string; id: string; value: string };

const LabelMenuOption = ({
  item,
  onSelect,
  isSelected,
}: {
  item: LabelOption;
  onSelect: (value: LabelOption) => void;
  isSelected?: boolean;
}) => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [title, setTitle] = React.useState(item.label);
  const titleInputRef = React.useRef<HTMLInputElement>(null);

  const taskId = useTasksStore((s) => s.currentTaskId);
  const task = useTasksStore(
    useCallback(
      (s) => {
        if (taskId) {
          return s.tasks[taskId];
        }
      },
      [taskId]
    )
  );

  const { editLabel, deleteLabel } = useBoardStore((s) => ({
    editLabel: s.actions.editLabel,
    deleteLabel: s.actions.deleteLabel,
  }));

  React.useEffect(() => {
    setTitle(item.label);
  }, [item.label]);

  const handleOpenMenu = () => {
    setTitle(item.label);
    setIsMenuOpen(true);
    setTimeout(() => {
      titleInputRef.current?.focus();
    }, 100);
  };

  const handleCloseMenu = () => {
    setIsMenuOpen(false);
    // if title is empty, don't save
    if (!title) return;
    // save title
    handleChangeLabel({ ...item, label: title }, 'edit');
  };

  const handleChangeLabel = React.useCallback(
    _.debounce((label: LabelOption, action: 'edit' | 'delete') => {
      if (!label) return;
      if (action === 'delete') {
        return deleteLabel({ labelId: label.id, boardId: task?.board?.id || '' });
      }
      editLabel({
        id: item.id,
        label: label.label,
        color: label.color,
        boardId: task?.board?.id || '',
        taskId: task?.id || '',
      });
    }, 300),
    []
  );

  const handleOptionSelect = (value) => {
    setTimeout(() => {
      if (value === 'delete') {
        handleChangeLabel(item, 'delete');
      } else {
        handleChangeLabel({ ...item, color: value, label: title }, 'edit');
      }
    }, 150);
  };

  return (
    <span
      className={cn(
        'flex items-center gap-8 justify-between px-12 py-4 text-sm hover:bg-gray-50 cursor-pointer',
        {
          'bg-gray-50': isMenuOpen,
        }
      )}
      onClick={() => onSelect(item)}
    >
      <div className="flex items-center gap-8 flex-grow min-w-0">
        <CheckThinIcon
          className={cn('min-w-0', {
            'opacity-0': !isSelected,
          })}
          size={20}
        />
        <div className="flex flex-grow min-w-0">
          <div className="max-w-full truncate rounded-lg px-6" style={{ background: item.color }}>
            {item.label}
          </div>
        </div>
      </div>

      <span onClick={(e) => e.stopPropagation()}>
        <MoreActionMenu
          onClickMenuItem={(item) => handleOptionSelect(item.value)}
          isOpen={isMenuOpen}
          onOpenMenu={handleOpenMenu}
          onCloseMenu={handleCloseMenu}
          headerData={
            <input
              className="mx-2 my-6 p-4 border rounded"
              ref={titleInputRef}
              value={title}
              placeholder="Label"
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleCloseMenu();
                }
              }}
            />
          }
          menuItems={[
            {
              label: (
                <div className="flex font-regular text-gray-999 text-sm ">
                  <DeleteIcon wrapperClass="mt-4 text-gray-500" />
                  &nbsp;&nbsp; Delete
                </div>
              ),
              value: 'delete',
            },
            ...Object.keys(randomColor).map((colorKey) => {
              const color = randomColor[colorKey];
              return {
                label: (
                  <ColumnToolbarLabel
                    color={randomColorClass[color]}
                    borderColor="border-gray-300"
                    labelColor={colorKey}
                  />
                ),
                value: color,
              };
            }),
          ]}
        />
      </span>
    </span>
  );
};
